import { Form, Upload } from "antd";
import { useEffect, useState } from "react";
import { FileImageFilled, UploadOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { t } from "i18next";
import Selector from "components/Selector/Selector";
import PrimaryButton from "components/Buttons/PrimaryButton/PrimaryButton";
import { audienceItems } from "helperFunctions/audience";
import sampleCsv from "assets/csv/sample.csv";
import { downloadFileFromUrl } from "helperFunctions/common";
import {
  setFilterInfoData
} from "redux/features/audience/queryPayloadSlice";
import centralApi from "services/centralApi";
import { getToken } from "redux/features/app/authTokenSlice";
import { FilterInfo } from "utils/models";
import { FormInstance } from "antd/lib";
import useMessage from "hooks/useMessage";
import { API_ENDPOINTS } from "utils/constants";

function FileCondition({
  form,
  condition,
  parentIndex,
  index,
}: {
  form: FormInstance;
  condition: FilterInfo;
  parentIndex: number;
  index: number;
}) {
  const { data: trait } = useSelector((state: any) => state.queryPayload);
  const loginUser = useSelector((state: any) => state.loginUser.data);
  const { appId } = useSelector((state: any) => state.activeApp);
  const { showError } = useMessage();
  const dispatch: any = useDispatch();
  const [formData, setFormData] = useState<any>({
    api_key: loginUser.api_key,
    app_id: appId,
    customUpload: true,
    file: null,
    datatype: undefined,
    fileheader: undefined,
    csvFile_id: "",
    audid: trait.audid,
    email: loginUser.email,
    clientid: "",
    did: "",
    category_type: undefined,
  });

  const handleChange = async (payload: { [key: string]: any }) => {
    let data = { ...payload };
    if (data.category_type || data.datatype) {
      data = {
        ...payload,
        clientid: data.datatype == "cid" ? "true" : "false",
        did: data.datatype == "did" ? "true" : "false",
      };
    }
    setFormData((prevState: any) => ({ ...prevState, ...data }));
    if (
      data.file &&
      data.csvFile_id &&
      formData.fileheader &&
      formData.datatype
    ) {
      uploadFile({ ...formData, ...data });
    } else {
      form.resetFields([`csv[${parentIndex}][${index}]`]);
    }
  };

  /**
   * Handles changes in the form data.
   * @param data - An object representing the  form data. Each key is a string, and values can be a string, array of strings, boolean, or undefined.
   */
  const handleFormChange = async (data: {
    [key: string]: string | string[] | boolean | undefined;
  }) => {
    await dispatch(
      setFilterInfoData({ data: { ...condition, ...data }, parentIndex, index })
    );
  };
  const uploadFile = async (payload: typeof formData) => {
    try {
      const data = new FormData();
      for (const key in payload) {
        if (payload[key] !== undefined) {
          data.set(key, payload[key]);
        }
      }
      const authToken = await dispatch(getToken()).unwrap();
      const queueInfo = await centralApi(
        "POST",
        API_ENDPOINTS.UPLOAD_SEGMENT_CSV_URL,
        data,
        {
          api_key: payload.api_key,
          app_id: payload.app_id,
          authToken,
        }
      );
      handleFormChange({ fileInfo: queueInfo });
    } catch (error) {
      handleChange({ file: null, csvFile_id: "" });
      showError(t("errorWhileUploadingMsg"));
    }
  };
  useEffect(() => {
    if (Object.keys(condition?.fileInfo || {}).length > 0) {
      (async () => {
        const { header, datatype, fileName } =
          condition?.fileInfo?.metadata?.dataObject || {};
        const file = new File([""], fileName, { type: "text/csv" });
        handleChange({
          fileheader: header,
          datatype,
          category_type: datatype,
          file
        });
        form.setFieldsValue({
          [`fileheader[${parentIndex}][${index}]`]: header,
          [`category_type[${parentIndex}][${index}]`]: datatype,
          [`csv[${parentIndex}][${index}]`]: file,
        });
      })();
    }
  }, [condition.fileInfo]);
  useEffect(() => {
    return () => {
      form.resetFields();
    };
  }, []);

  return (
    <div className="d-flex gap-2 w-100">
      <Form.Item
        className="m-0 input-size-sm"
        name={`fileheader[${parentIndex}][${index}]`}
        initialValue={formData.fileheader}
        rules={[{ required: true, message: `${t("plzSelectHeaderMsg")}` }]}
      >
        <Selector
          onChange={async (value) => {
            await handleChange({
              fileheader: value,
              category_type: undefined,
              datatype: undefined,
              clientid: undefined,
              did: undefined,
            });
            form.resetFields([`category_type[${parentIndex}][${index}]`]);
          }}
          placeholder={`${t("fileHeaderMsg")}`}
          options={audienceItems.fileHeaderOptions}
          value={formData.fileheader}
        ></Selector>
      </Form.Item>
      <Form.Item
        className="m-0 input-size-sm"
        name={`category_type[${parentIndex}][${index}]`}
        initialValue={formData.category_type}
        rules={[{ required: true, message: `${t("plzSelectTypeOfDataMsg")}` }]}
      >
        <Selector
          onChange={(value) => {
            handleChange({
              category_type: value,
              datatype: value,
              file: undefined,
              csvFile_id: undefined,
            });
          }}
          placeholder={`${t("typeOfDataMsg")}`}
          value={formData.category_type}
          options={audienceItems.typeOfDataOptions}
        ></Selector>
      </Form.Item>
      <Form.Item
        className="m-0"
        name={`csv[${parentIndex}][${index}]`}
        rules={[{ required: true, message: `${t("chooseCsvFileMsg")}` }]}
        valuePropName="file"
        getValueFromEvent={(e) => e.fileList}
      >
        <Upload
          showUploadList={false}
          accept=".csv"
          multiple={false}
          onChange={(value) => {
            handleChange({ file: value.file, csvFile_id: value.file.name });
          }}
          beforeUpload={() => false}
        >
          <PrimaryButton
            disabled={!formData?.datatype || !formData?.fileheader}
          >
            {formData.file ? <FileImageFilled /> : <UploadOutlined />}

            <span className="mx-1">
              {formData.file ? formData.file.name : t("chooseCsvFileMsg")}
            </span>
          </PrimaryButton>
        </Upload>
      </Form.Item>
      <Form.Item className="m-0">
        <PrimaryButton
          onClick={() => downloadFileFromUrl(sampleCsv, "sample.csv")}
        >
          {t("sampleCsvLabel")}
        </PrimaryButton>
      </Form.Item>
    </div>
  );
}

export default FileCondition;
